import { type, type Type } from 'arktype';
import { isSameDecimal } from '../lib/decimal-math';
import { absenceRangeError } from './absence-payload.model';
import { dayKeySchema } from './day-key.model';
import { overviewIdSchema } from './overview.model';
import { narrowError } from './schema-narrow';
import { absencePortionSchema, portionCouplingError } from './timetracking-absence.model';

export const countDaysReasons = ['weekend', 'holiday', 'nonWorkday', 'outsideEmployment', 'occupied', 'locked'] as const;
export type CountDaysReason = (typeof countDaysReasons)[number];

export const countDaysRequestSchema = type({
  'personId?': overviewIdSchema,
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  'absenceType?': 'string > 0',
  'excludeEntryId?': 'string > 0',
})
  .and(absencePortionSchema)
  .narrow(narrowError(portionCouplingError))
  .narrow(narrowError(absenceRangeError))
  .onUndeclaredKey('reject');
export type CountDaysRequestInput = typeof countDaysRequestSchema.t;

function countDaysPerDayError(day: { counts: boolean; weight: number; reason?: CountDaysReason }): string | null {
  if (day.counts) return day.weight > 0 && day.reason === undefined ? null : 'a counted day with a positive weight and no reason';
  return day.weight === 0 && day.reason !== undefined ? null : 'an uncounted day with weight 0 and a reason';
}

export interface CountDaysPerDay {
  date: string;
  counts: boolean;
  weight: number;
  reason?: CountDaysReason;
}

export const countDaysPerDaySchema: Type<CountDaysPerDay> = type({
  date: dayKeySchema,
  counts: 'boolean',
  weight: 'number >= 0',
  'reason?': type.enumerated(...countDaysReasons),
})
  .narrow(narrowError(countDaysPerDayError))
  .onUndeclaredKey('reject');

function countDaysResponseError(response: {
  total: number;
  perDay: readonly { weight: number }[];
  balancePreview?: { before: number; after: number };
}): string | null {
  const counted = response.perDay.reduce((sum, day) => sum + day.weight, 0);
  if (!isSameDecimal(response.total, counted)) return 'a total equal to the summed day weights';
  const preview = response.balancePreview;
  return preview === undefined || isSameDecimal(preview.after, preview.before - response.total)
    ? null
    : 'a preview after equal to before - total';
}

export interface CountDaysResponse {
  total: number;
  perDay: CountDaysPerDay[];
  workingWeekdays: number[];
  balancePreview?: { before: number; after: number };
}

export const countDaysResponseSchema: Type<CountDaysResponse> = type({
  total: 'number >= 0',
  perDay: countDaysPerDaySchema.array(),
  workingWeekdays: type('0 | 1 | 2 | 3 | 4 | 5 | 6').array(),
  'balancePreview?': type({ before: 'number', after: 'number' }),
})
  .narrow(narrowError(countDaysResponseError))
  .onDeepUndeclaredKey('reject');
